/**
 * AddMedicationDialog Entry Point
 * 
 * This component provides a comprehensive dialog for adding medications using a step-by-step process.
 * It implements a responsive design with different layouts for mobile and desktop.
 * 
 * Features:
 * - Mobile-first, responsive design
 * - Four-step guided workflow
 * - Form validation at each step
 * - Accessible UI with proper keyboard navigation
 * - Visual feedback for progress
 * - Summary of entered information
 * 
 * @file src/components/common/AddMedicationDialog.tsx
 * @description Entry point for the medication dialog component
 */

import React from 'react';
import AddMedicationModal from '@/components/medication/modal/AddMedicationModal';
import { MedicationFormData } from '@/components/medication/types/medication';

// Define props interface for the dialog component
export interface AddMedicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddMedication?: (medication: MedicationFormData) => void;
  editMedication?: MedicationFormData;
  insideDialogContent?: boolean; // Flag to indicate this is already inside a DialogContent
}

// Define medication summary type for convenience
export interface MedicationSummary {
  id: string;
  name: string;
  dosage: string;
  form: string;
  frequency: string;
  timeOfDay: string[];
  dosesAtTime: Record<string, number>;
  hasCycle: boolean;
  cycleWeeksOn?: number;
  cycleWeeksOff?: number;
  foodInstructions?: string;
  color?: string;
}

/**
 * AddMedicationDialog component
 * 
 * A wrapper around the AddMedicationModal to maintain backward compatibility.
 * 
 * @param open - Dialog open state
 * @param onOpenChange - Function to handle dialog open/close state changes
 * @param onAddMedication - Optional callback when medication is added/updated
 * @param editMedication - Optional medication to edit
 */
export function AddMedicationDialog({ 
  open, 
  onOpenChange, 
  onAddMedication, 
  editMedication,
  insideDialogContent = false
}: AddMedicationDialogProps) {
  // Handler for successful medication submission
  const handleSuccess = (medicationData: any) => {
    const event = new CustomEvent('meduler:medication-added', {
      bubbles: true,
      composed: true,
      cancelable: false,
      detail: { medication: medicationData, timestamp: Date.now() }
    });

    window.dispatchEvent(event);

    document.dispatchEvent(new CustomEvent('meduler:medication-added', {
      bubbles: true,
      composed: true,
      detail: { medication: medicationData, timestamp: Date.now() }
    }));

    if (onAddMedication) {
      onAddMedication(medicationData);
    }
  };
  
  // If we're already inside a DialogContent, we need to ensure DialogTitle is present
  if (insideDialogContent) {
    return (
      <>
        {/* CRITICAL: Always include DialogTitle for accessibility when inside DialogContent */}
        <div className="text-xl px-4 py-2 font-semibold text-left sr-only" aria-live="polite">
          <h2>Add Medication</h2>
        </div>
        
        <AddMedicationModal
          isOpen={open}
          onClose={() => onOpenChange(false)}
          editMedication={editMedication}
          onSuccess={handleSuccess}
          insideDialogContent={true}
        />
      </>
    );
  }
  
  // Standard usage - let AddMedicationModal handle its own Dialog/DialogContent
  return (
    <AddMedicationModal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      editMedication={editMedication}
      onSuccess={handleSuccess}
      insideDialogContent={false}
    />
  );
}

// Default export for backwards compatibility
export default AddMedicationDialog;
